/**
 * Money helpers per phase1-fe-be-contract.md §3.2.
 * BE returns Money as `{ amount: number, currencyCode: string }` (ISO 4217).
 * FE formatting uses `Intl.NumberFormat` — no external dep.
 */
export interface Money {
  amount: number;
  currencyCode: string; // ISO 4217 — 'KRW', 'USD', ...
}

export interface MoneyRange {
  min: number;
  max: number;
  currencyCode: string;
}

const ZERO_FRACTION_CURRENCIES = new Set(['KRW', 'JPY', 'VND']);

export const formatMoney = (
  money: Money,
  locale: string = 'ko-KR',
  opts: { style?: 'currency' | 'decimal'; signDisplay?: 'auto' | 'always' | 'never' } = {},
): string => {
  const fractionDigits = ZERO_FRACTION_CURRENCIES.has(money.currencyCode) ? 0 : 2;
  // figma 정본: ko-KR + KRW 는 "17,800원" suffix (₩17,800 아님). 다른 locale/currency 는 표준 currency style.
  if (
    money.currencyCode === 'KRW' &&
    locale === 'ko-KR' &&
    (opts.style ?? 'currency') === 'currency'
  ) {
    const decimal = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      signDisplay: opts.signDisplay ?? 'auto',
    }).format(money.amount);
    return `${decimal}원`;
  }
  return new Intl.NumberFormat(locale, {
    style: opts.style ?? 'currency',
    currency: money.currencyCode,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay: opts.signDisplay ?? 'auto',
  }).format(money.amount);
};

export const formatMoneyRange = (
  range: MoneyRange,
  locale: string = 'ko-KR',
): string => {
  if (range.min === range.max) {
    return formatMoney({ amount: range.min, currencyCode: range.currencyCode }, locale);
  }
  const lo = formatMoney({ amount: range.min, currencyCode: range.currencyCode }, locale);
  const hi = formatMoney({ amount: range.max, currencyCode: range.currencyCode }, locale);
  return `${lo} – ${hi}`;
};

export const addMoney = (a: Money, b: Money): Money => {
  if (a.currencyCode !== b.currencyCode) {
    throw new Error(`Cannot add money in different currencies: ${a.currencyCode} vs ${b.currencyCode}`);
  }
  return { amount: a.amount + b.amount, currencyCode: a.currencyCode };
};

export const isZeroMoney = (m: Money): boolean => m.amount === 0;
